/**
 * ResumeBuilder — /profile/resume-builder
 *
 * Standalone resume editor for the SocialPulse Resume Service. Loads
 * /api/resume/me, lets the user edit every section, import from
 * LinkedIn (ZIP or PDF), pick 1 of 6 templates, toggle visibility, and
 * download / share the PDF. Sister sites (alumnipulse.net,
 * indiaalumni.com, indianedupulse.com, career hubs on the geographical
 * portals) consume the same data via GET /api/resume/{handle} — no
 * duplicate editors.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { http, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Download, Save, Upload, Loader2, Plus, Trash2, Eye, EyeOff, Globe,
  Users, Lock, ExternalLink, ChevronLeft, Palette,
} from "lucide-react";

const TEMPLATES = [
  { key: "classic",     name: "Classic",     tagline: "Clean, timeless, ATS-safe." },
  { key: "modern",      name: "Modern",      tagline: "Two-tone with a monogram." },
  { key: "executive",   name: "Executive",   tagline: "Formal serif with a centred header." },
  { key: "creative",    name: "Creative",    tagline: "Bold sidebar with an accent panel." },
  { key: "minimalist",  name: "Minimalist",  tagline: "Thin type, generous whitespace." },
  { key: "academic",    name: "Academic",    tagline: "CV-style, publications-first." },
];

const VIS_OPTIONS = [
  { key: "public",   label: "Public",  icon: Globe,  hint: "Anyone can view + download the PDF." },
  { key: "network",  label: "Network", icon: Users,  hint: "Only authorised SocialPulse sister sites." },
  { key: "private",  label: "Private", icon: Lock,   hint: "Only you can see it." },
];

const ACCENTS = ["#4f46e5","#059669","#dc2626","#0891b2","#7c3aed","#ea580c","#111827"];

const empty = {
  contact: { name: "", headline: "", email: "", phone: "", location: "", website: "", linkedin: "", github: "", twitter: "" },
  summary: "", experience: [], education: [], skills: [], projects: [],
  certifications: [], languages: [], awards: [], publications: [], volunteering: [],
  custom_sections: [],
  visibility: "public", template: "modern", accent_color: "#4f46e5", font_family: "sans",
};

export default function ResumeBuilder() {
  const { user, ready } = useAuth();
  const [loading, setLoading] = useState(true);
  const [resume, setResume] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFormat, setImportFormat] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!ready) return;
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const r = await http.get("/resume/me");
        setResume({ ...empty, ...r.data.resume });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't load your resume");
      } finally {
        setLoading(false);
      }
    })();
  }, [ready, user]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await http.patch("/resume/me", patch);
      setResume(r.data.resume);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveField = (key) => (val) => setResume(p => ({ ...p, [key]: val }));
  const saveContact = (field) => (e) => setResume(p => ({ ...p, contact: { ...p.contact, [field]: e.target.value } }));

  const pdfUrl = useMemo(() => {
    if (!resume?.handle) return "";
    return `${API}/resume/${resume.handle}/pdf?template=${resume.template}&t=${Date.now()}`;
  }, [resume.handle, resume.template]);

  const publicUrl = useMemo(() => {
    if (!resume?.handle) return "";
    return `${window.location.origin}/${resume.handle}/resume`;
  }, [resume.handle]);

  const uploadLinkedIn = async (file) => {
    setImporting(true);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post("/resume/me/import/linkedin", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportFormat(r.data.format);
      setImportPreview(r.data.preview);
      toast.success(`Parsed ${r.data.format.toUpperCase()} — review below then Apply`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const applyImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const r = await http.post("/resume/me/import/linkedin/apply", {
        strategy: "merge", resume: importPreview,
      });
      setResume(r.data.resume);
      setImportPreview(null);
      toast.success("LinkedIn data merged");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally {
      setImporting(false);
    }
  };

  if (!ready || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  }
  if (!user) {
    return <div className="min-h-screen flex items-center justify-center text-slate-600">Please sign in to build your resume. <Link to="/login" className="ml-2 text-indigo-600 underline">Sign in</Link></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="resume-builder-page">
      {/* Header bar */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to={`/${resume.handle}`} className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm">
            <ChevronLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Resume Builder</div>
            <div className="text-xs text-slate-500">Standalone resume that syncs across every SocialPulse sister site.</div>
          </div>
          <a
            href={pdfUrl} target="_blank" rel="noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm text-slate-700 hover:bg-slate-50"
            data-testid="resume-download-pdf-btn"
          >
            <Download className="w-4 h-4" /> PDF
          </a>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid lg:grid-cols-3 gap-6">
        {/* Left column: editor sections */}
        <div className="lg:col-span-2 space-y-5">
          {/* Contact */}
          <SectionCard title="Contact" testid="section-contact">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Full name" value={resume.contact.name} onChange={saveContact("name")} testid="contact-name-input" />
              <Input label="Headline" value={resume.contact.headline} onChange={saveContact("headline")} placeholder="Software Engineer · Kochi → Global" testid="contact-headline-input" />
              <Input label="Email" value={resume.contact.email} onChange={saveContact("email")} testid="contact-email-input" />
              <Input label="Phone" value={resume.contact.phone} onChange={saveContact("phone")} testid="contact-phone-input" />
              <Input label="Location" value={resume.contact.location} onChange={saveContact("location")} testid="contact-location-input" />
              <Input label="Website" value={resume.contact.website} onChange={saveContact("website")} testid="contact-website-input" />
              <Input label="LinkedIn URL" value={resume.contact.linkedin} onChange={saveContact("linkedin")} testid="contact-linkedin-input" />
              <Input label="GitHub URL" value={resume.contact.github} onChange={saveContact("github")} testid="contact-github-input" />
            </div>
            <div className="mt-3 flex justify-end">
              <button onClick={() => save({ contact: resume.contact })} disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm disabled:opacity-50" data-testid="save-contact-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save contact
              </button>
            </div>
          </SectionCard>

          {/* Summary */}
          <SectionCard title="Summary / About" testid="section-summary">
            <textarea
              rows={4} className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="A 2-4 sentence pitch about who you are and what you're looking for."
              value={resume.summary}
              onChange={e => setResume(p => ({ ...p, summary: e.target.value }))}
              onBlur={() => save({ summary: resume.summary })}
              data-testid="summary-textarea"
            />
          </SectionCard>

          {/* Experience */}
          <RepeatableSection
            title="Experience" items={resume.experience || []}
            onChange={items => save({ experience: items })}
            fields={[
              { key: "title", label: "Title", cls: "sm:col-span-3" },
              { key: "company", label: "Company", cls: "sm:col-span-3" },
              { key: "location", label: "Location", cls: "sm:col-span-2" },
              { key: "start_date", label: "Start (YYYY-MM)", cls: "sm:col-span-2" },
              { key: "end_date", label: "End (blank = current)", cls: "sm:col-span-2" },
              { key: "description", label: "Description", cls: "sm:col-span-6", multiline: true },
            ]}
            emptyItem={() => ({ title: "", company: "", location: "", start_date: "", end_date: "", description: "", achievements: [] })}
            testid="section-experience"
          />

          {/* Education */}
          <RepeatableSection
            title="Education" items={resume.education || []}
            onChange={items => save({ education: items })}
            fields={[
              { key: "institution", label: "Institution", cls: "sm:col-span-4" },
              { key: "degree", label: "Degree", cls: "sm:col-span-2" },
              { key: "field", label: "Field of study", cls: "sm:col-span-3" },
              { key: "start_date", label: "Start (YYYY-MM)", cls: "sm:col-span-2" },
              { key: "end_date", label: "End (YYYY-MM)", cls: "sm:col-span-2" },
              { key: "gpa", label: "GPA", cls: "sm:col-span-1" },
              { key: "description", label: "Notes", cls: "sm:col-span-6", multiline: true },
            ]}
            emptyItem={() => ({ institution: "", degree: "", field: "", start_date: "", end_date: "", gpa: "", description: "" })}
            testid="section-education"
          />

          {/* Skills */}
          <SkillsEditor
            groups={resume.skills || []}
            onChange={skills => save({ skills })}
          />

          {/* Projects */}
          <RepeatableSection
            title="Projects" items={resume.projects || []}
            onChange={items => save({ projects: items })}
            fields={[
              { key: "name", label: "Project name", cls: "sm:col-span-4" },
              { key: "url", label: "URL", cls: "sm:col-span-2" },
              { key: "start_date", label: "Start", cls: "sm:col-span-3" },
              { key: "end_date", label: "End", cls: "sm:col-span-3" },
              { key: "description", label: "Description", cls: "sm:col-span-6", multiline: true },
            ]}
            emptyItem={() => ({ name: "", url: "", start_date: "", end_date: "", description: "", technologies: [] })}
            testid="section-projects"
          />

          {/* Certifications */}
          <RepeatableSection
            title="Certifications" items={resume.certifications || []}
            onChange={items => save({ certifications: items })}
            fields={[
              { key: "name", label: "Name", cls: "sm:col-span-3" },
              { key: "issuer", label: "Issuer", cls: "sm:col-span-2" },
              { key: "date", label: "Date (YYYY-MM)", cls: "sm:col-span-1" },
              { key: "url", label: "Verify URL", cls: "sm:col-span-3" },
              { key: "credential_id", label: "Credential ID", cls: "sm:col-span-3" },
            ]}
            emptyItem={() => ({ name: "", issuer: "", date: "", url: "", credential_id: "" })}
            testid="section-certifications"
          />

          {/* Awards */}
          <RepeatableSection
            title="Awards" items={resume.awards || []}
            onChange={items => save({ awards: items })}
            fields={[
              { key: "title", label: "Title", cls: "sm:col-span-3" },
              { key: "issuer", label: "Issuer", cls: "sm:col-span-2" },
              { key: "date", label: "Date", cls: "sm:col-span-1" },
              { key: "description", label: "Description", cls: "sm:col-span-6", multiline: true },
            ]}
            emptyItem={() => ({ title: "", issuer: "", date: "", description: "" })}
            testid="section-awards"
          />
        </div>

        {/* Right column: side panel */}
        <div className="space-y-5">
          {/* Visibility + PDF */}
          <SectionCard title="Share" testid="section-share">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Who can see it</div>
            <div className="space-y-2">
              {VIS_OPTIONS.map(o => {
                const Icon = o.icon;
                const active = resume.visibility === o.key;
                return (
                  <button
                    key={o.key}
                    onClick={() => save({ visibility: o.key })}
                    className={`w-full text-left border rounded-md px-3 py-2 flex items-start gap-2 ${active ? "border-indigo-600 bg-indigo-50" : "hover:bg-slate-50"}`}
                    data-testid={`visibility-${o.key}-btn`}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 ${active ? "text-indigo-600" : "text-slate-400"}`} />
                    <div>
                      <div className={`text-sm font-medium ${active ? "text-indigo-900" : "text-slate-900"}`}>{o.label}</div>
                      <div className="text-xs text-slate-500">{o.hint}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {publicUrl && resume.visibility === "public" && (
              <div className="mt-4 pt-3 border-t">
                <div className="text-xs text-slate-500 mb-1">Public URL</div>
                <div className="flex items-center gap-1 text-sm text-indigo-700">
                  <a href={publicUrl} target="_blank" rel="noreferrer" className="truncate hover:underline flex-1">{publicUrl}</a>
                  <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }} className="p-1 hover:bg-slate-100 rounded" data-testid="copy-public-url-btn">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
            <a
              href={pdfUrl} target="_blank" rel="noreferrer"
              className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-900 text-white rounded-md text-sm hover:bg-slate-800"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4" /> Download PDF
            </a>
          </SectionCard>

          {/* Template picker */}
          <SectionCard title="Template" testid="section-template">
            <div className="space-y-2">
              {TEMPLATES.map(t => {
                const active = resume.template === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => save({ template: t.key })}
                    className={`w-full text-left border rounded-md px-3 py-2 ${active ? "border-indigo-600 bg-indigo-50" : "hover:bg-slate-50"}`}
                    data-testid={`template-${t.key}-btn`}
                  >
                    <div className={`text-sm font-medium ${active ? "text-indigo-900" : "text-slate-900"}`}>{t.name}</div>
                    <div className="text-xs text-slate-500">{t.tagline}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-4 pt-3 border-t">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Palette className="w-3 h-3" /> Accent</div>
              <div className="flex flex-wrap gap-2">
                {ACCENTS.map(c => (
                  <button
                    key={c}
                    onClick={() => save({ accent_color: c })}
                    className={`w-7 h-7 rounded-full border-2 ${resume.accent_color === c ? "border-slate-900" : "border-transparent"}`}
                    style={{ background: c }}
                    data-testid={`accent-${c.slice(1)}-btn`}
                  />
                ))}
              </div>
            </div>
          </SectionCard>

          {/* LinkedIn import */}
          <SectionCard title="Import from LinkedIn" testid="section-linkedin">
            <p className="text-xs text-slate-600 mb-3">
              Either upload your <b>LinkedIn data ZIP</b> (Settings → Get a copy of your data)
              <i> or</i> your <b>LinkedIn PDF resume</b> (Save to PDF on your profile). We'll parse
              it and let you review before merging.
            </p>
            <input
              ref={fileInputRef}
              type="file" className="hidden"
              accept=".zip,.pdf,application/zip,application/pdf"
              onChange={e => { const f = e.target.files?.[0]; if (f) uploadLinkedIn(f); e.target.value = ""; }}
              data-testid="linkedin-file-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 border-2 border-dashed border-slate-300 rounded-md text-sm hover:bg-slate-50 disabled:opacity-50"
              data-testid="linkedin-upload-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {importing ? "Parsing…" : "Upload LinkedIn export"}
            </button>
            {importPreview && (
              <div className="mt-3 p-3 border rounded-md bg-emerald-50">
                <div className="text-xs uppercase tracking-wider text-emerald-800 mb-1">Preview ({importFormat.toUpperCase()})</div>
                {importPreview.contact?.name && <div className="text-sm"><b>Name:</b> {importPreview.contact.name}</div>}
                {importPreview.experience?.length > 0 && <div className="text-sm">{importPreview.experience.length} experience item(s)</div>}
                {importPreview.education?.length > 0 && <div className="text-sm">{importPreview.education.length} education item(s)</div>}
                {importPreview.skills?.[0]?.items?.length > 0 && <div className="text-sm">{importPreview.skills[0].items.length} skills</div>}
                <button
                  onClick={applyImport} disabled={importing}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-md text-sm disabled:opacity-50"
                  data-testid="linkedin-apply-btn"
                >
                  {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  Merge into resume
                </button>
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}


// ─── Reusable pieces ────────────────────────────────────────────────


function SectionCard({ title, children, testid }) {
  return (
    <div className="bg-white border rounded-lg p-4" data-testid={testid}>
      <div className="text-sm font-semibold text-slate-900 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs text-slate-500">{label}</span>
      <input
        type="text" value={value || ""} onChange={onChange} placeholder={placeholder}
        className="mt-0.5 w-full border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
        data-testid={testid}
      />
    </label>
  );
}

function RepeatableSection({ title, items, onChange, fields, emptyItem, testid }) {
  const update = (i, key, val) => {
    const next = items.map((it, ii) => ii === i ? { ...it, [key]: val } : it);
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, ii) => ii !== i));
  const add = () => onChange([...items, emptyItem()]);

  return (
    <div className="bg-white border rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <button onClick={add} className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1" data-testid={`${testid}-add-btn`}>
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      {items.length === 0 && <div className="text-xs text-slate-400 py-6 text-center">No entries yet. Click "Add" to start.</div>}
      {items.map((it, i) => (
        <div key={i} className="border-b last:border-b-0 pb-3 pt-3 first:pt-0">
          <div className="flex justify-end mb-1">
            <button onClick={() => remove(i)} className="text-xs text-slate-400 hover:text-red-600 p-0.5" data-testid={`${testid}-remove-${i}-btn`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="grid sm:grid-cols-6 gap-2">
            {fields.map(f => (
              <div key={f.key} className={f.cls}>
                <label className="block">
                  <span className="text-xs text-slate-500">{f.label}</span>
                  {f.multiline ? (
                    <textarea rows={2}
                      value={it[f.key] || ""}
                      onChange={e => update(i, f.key, e.target.value)}
                      className="mt-0.5 w-full border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  ) : (
                    <input type="text"
                      value={it[f.key] || ""}
                      onChange={e => update(i, f.key, e.target.value)}
                      className="mt-0.5 w-full border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  )}
                </label>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SkillsEditor({ groups, onChange }) {
  // Simplify: one group called "Skills" as a comma-separated list.
  const items = (groups?.[0]?.items) || [];
  const setItems = (arr) => onChange([{ category: "Skills", items: arr }]);
  const [text, setText] = useState(items.join(", "));
  useEffect(() => { setText(items.join(", ")); /* eslint-disable-next-line */ }, [groups]);
  return (
    <div className="bg-white border rounded-lg p-4" data-testid="section-skills">
      <div className="text-sm font-semibold text-slate-900 mb-2">Skills</div>
      <textarea rows={2}
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={() => setItems(text.split(",").map(s => s.trim()).filter(Boolean))}
        placeholder="React, Python, MongoDB, Product design, …"
        className="w-full border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
        data-testid="skills-input"
      />
      <div className="text-xs text-slate-500 mt-1">Comma-separated. Save happens on blur.</div>
    </div>
  );
}
